import React, { useState, useEffect } from "react";
import {
	Button,
	Checkbox,
	Col,
	Flex,
	Form,
	Input,
	Row,
	Select,
	Upload,
	Typography,
} from "antd";
import { CustomSpinner } from "../utils/CustomComponents";
import { UploadOutlined } from "@ant-design/icons";

const jobTypeOptions = [
	{ value: "Municipal", label: "Municipal" },
	{ value: "State", label: "State" },
	{ value: "Federal", label: "Federal" },
	{ value: "Commercial", label: "Commercial" },
	{ value: "Residential", label: "Residential" },
];

const { Title } = Typography;

const JobProjectSheet = ({ job }) => {
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(true);
	const [initialValues, setInitialValues] = useState({});

	useEffect(() => {
		assignData();
	}, []);

	const assignData = async () => {
		setInitialValues({
			projectJobName: job.projectJobName,
			projectJobDescription: job.projectJobDescription,
			projectApContactEmail: job.projectApContactEmail,
			projectJobType: job.projectJobType,
			projectTaxExemptJob: job.projectTaxExemptJob,
			projectBond: job.projectBond,
			projectTaxExemptEvidence: job.projectTaxExemptEvidence
				? [
						{
							uid: job.projectTaxExemptEvidence.split("/")[6],
							name: job.projectTaxExemptEvidence.split("/")[6],
							url: job.projectTaxExemptEvidence,
							status: "done",
						},
				  ]
				: [],
			projectPONumber: job.projectPONumber,
			projectGeneralContractorName: job.projectGeneralContractorName,
			projectGeneralContractorContactName:
				job.projectGeneralContractorContactName,
			projectGeneralContractorPhone: job.projectGeneralContractorPhone
				? job.projectGeneralContractorPhone.substring(0, 2) === "+1"
					? job.projectGeneralContractorPhone.substring(2)
					: job.projectGeneralContractorPhone
				: undefined,
			projectGeneralContractorAddress: job.projectGeneralContractorAddress,
			projectOwnerName: job.projectOwnerName,
			projectOwnerPhone: job.projectOwnerPhone
				? job.projectOwnerPhone.substring(0, 2) === "+1"
					? job.projectOwnerPhone.substring(2)
					: job.projectOwnerPhone
				: undefined,
			projectOwnerAddress: job.projectOwnerAddress,
			projectSurety: job.projectSurety,
			projectSuretyPhone: job.projectSuretyPhone
				? job.projectSuretyPhone.substring(0, 2) === "+1"
					? job.projectSuretyPhone.substring(2)
					: job.projectSuretyPhone
				: undefined,
			projectSuretyAddress: job.projectSuretyAddress,
			projectBondNumber: job.projectBondNumber,
			projectBondEvidence: job.projectBondEvidence
				? [
						{
							uid: job.projectBondEvidence.split("/")[6],
							name: job.projectBondEvidence.split("/")[6],
							url: job.projectBondEvidence,
							status: "done",
						},
				  ]
				: [],
			customerName: job.customer.name,
			apContact: job.apContact,
			apPhone: job.apPhone
				? job.apPhone.substring(0, 2) === "+1"
					? job.apPhone.substring(2)
					: job.apPhone
				: undefined,
		});
		setLoading(false);
	};

	const normFile = (e) => {
		if (Array.isArray(e)) {
			return e;
		}
		return e?.fileList;
	};

	const prefixSelector = (
		<Form.Item name="prefix" noStyle>
			<Select
				style={{
					width: 70,
				}}
				defaultValue={"+1"}
			>
				<Select.Option value="+1">+1</Select.Option>
			</Select>
		</Form.Item>
	);

	const CustomForm = () => (
		<Form
			form={form}
			layout="vertical"
			style={{ marginTop: "20px" }}
			initialValues={initialValues}
		>
			<Row justify={"center"}>
				<Title level={1}>JOB Sheet Info</Title>
			</Row>
			<Row gutter={16}>
				<Col span={12}>
					<Form.Item
						name="projectJobName"
						label="Name of Job"
						rules={[{ required: true, message: "Please enter a job name" }]}
					>
						<Input size="large" disabled />
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						name="customerName"
						label="Customer Name"
						rules={[
							{ required: true, message: "Please enter a customer name" },
						]}
					>
						<Input size="large" disabled />
					</Form.Item>
				</Col>
				<Col span={24}>
					<Form.Item
						name="projectJobDescription"
						label="Job Description"
						rules={[{ required: true, message: "Please enter a description" }]}
					>
						<Input size="large" disabled />
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						name="apContact"
						label="AP Contact Name"
						rules={[
							{
								required: true,
								message: "Please enter the AP contact name",
							},
						]}
					>
						<Input size="large" disabled />
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						name="projectApContactEmail"
						label="AP Contact Email"
						rules={[
							{
								required: true,
								message: "Please enter the AP contact email",
							},
							{
								type: "email",
								message: "Please enter a valid email",
							},
						]}
					>
						<Input size="large" disabled />
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						name="apPhone"
						label="AP Contact Phone"
						rules={[
							{
								required: true,
								message: "Please enter the AP contact phone",
							},
							{
								len: 10,
								max: 10,
								message: "Phone number must have 10 numbers",
							},
						]}
					>
						<Input size="large" addonBefore={prefixSelector} disabled />
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						label="Job Type"
						name="projectJobType"
						rules={[
							{
								required: true,
								message: "Please select job type",
							},
						]}
					>
						<Select
							style={{
								width: "100%",
							}}
							size="large"
							placeholder="Please select job type"
							options={jobTypeOptions}
							optionFilterProp="children"
							disabled
						/>
					</Form.Item>
				</Col>
				<Col span={8}>
					<Form.Item
						label="Tax Exempt Job"
						name="projectTaxExemptJob"
						valuePropName="checked"
					>
						<Checkbox disabled>Yes/No</Checkbox>
					</Form.Item>
				</Col>
				<Form.Item
					noStyle
					shouldUpdate={(prevValues, currentValues) =>
						prevValues.projectTaxExemptJob !== currentValues.projectTaxExemptJob
					}
				>
					{({ getFieldValue }) =>
						getFieldValue("projectTaxExemptJob") && (
							<Col span={4}>
								<Form.Item
									label="Evidence"
									name="projectTaxExemptEvidence"
									valuePropName="fileList"
									getValueFromEvent={normFile}
									rules={[
										{
											required: true,
											message: "Evidence is required",
										},
									]}
								>
									<Upload
										beforeUpload={() => false}
										maxCount={1}
										multiple={false}
										accept=".png, .jpg, .jpeg, .pdf"
										disabled={true}
									>
										<Button
											icon={<UploadOutlined />}
											size="large"
											style={{
												width: "100%",
											}}
											type="primary"
											disabled
										>
											Upload
										</Button>
									</Upload>
								</Form.Item>
							</Col>
						)
					}
				</Form.Item>
				<Col span={12}>
					<Form.Item
						name="projectPONumber"
						label="PO #"
						rules={[
							{
								required: true,
								message: "Please enter the PO number",
							},
						]}
					>
						<Input size="large" disabled />
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						name="projectGeneralContractorName"
						label="General Contractor Name"
						rules={[
							{
								required: true,
								message: "Please enter the General Contractor Name",
							},
						]}
					>
						<Input size="large" disabled />
					</Form.Item>
				</Col>
				<Col span={12}></Col>
				<Col span={12}>
					<Form.Item
						name="projectGeneralContractorContactName"
						label="General Contractor Contact Name"
						rules={[
							{
								required: true,
								message: "Please enter the General Contractor Contact Name",
							},
						]}
					>
						<Input size="large" disabled />
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						name="projectGeneralContractorPhone"
						label="General Contractor Phone"
						rules={[
							{
								required: true,
								message: "Please enter the General Contractor Phone",
							},
							{
								len: 10,
								max: 10,
								message: "Phone number must have 10 numbers",
							},
						]}
					>
						<Input size="large" addonBefore={prefixSelector} disabled />
					</Form.Item>
				</Col>
				<Col span={24}>
					<Form.Item
						name="projectGeneralContractorAddress"
						label="Address"
						rules={[
							{
								required: true,
								message: "Please enter the address",
							},
						]}
					>
						<Input size="large" disabled />
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						name="projectOwnerName"
						label="Owner Name"
						rules={[
							{
								required: true,
								message: "Please enter the owner name",
							},
						]}
					>
						<Input size="large" disabled />
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						name="projectOwnerPhone"
						label="Owner Phone"
						rules={[
							{
								required: true,
								message: "Please enter the Owner Phone",
							},
							{
								len: 10,
								max: 10,
								message: "Phone number must have 10 numbers",
							},
						]}
					>
						<Input size="large" addonBefore={prefixSelector} disabled />
					</Form.Item>
				</Col>
				<Col span={24}>
					<Form.Item
						name="projectOwnerAddress"
						label="Owner Address"
						rules={[
							{
								required: true,
								message: "Please enter the owner address",
							},
						]}
					>
						<Input size="large" disabled />
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						name="projectSurety"
						label="Surety"
						rules={[
							{
								required: true,
								message: "Please enter the surety",
							},
						]}
					>
						<Input size="large" disabled />
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						name="projectSuretyPhone"
						label="Surety Phone"
						rules={[
							{
								required: true,
								message: "Please enter the Surety Phone",
							},
							{
								len: 10,
								max: 10,
								message: "Phone number must have 10 numbers",
							},
						]}
					>
						<Input size="large" addonBefore={prefixSelector} disabled />
					</Form.Item>
				</Col>
				<Col span={24}>
					<Form.Item
						name="projectSuretyAddress"
						label="Surety Address"
						rules={[
							{
								required: true,
								message: "Please enter the surety address",
							},
						]}
					>
						<Input size="large" disabled />
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						name="projectBondNumber"
						label="Bond #"
						rules={[
							{
								required: true,
								message: "Please enter the bond number",
							},
						]}
					>
						<Input size="large" disabled />
					</Form.Item>
				</Col>
				<Col span={8}>
					<Form.Item
						label="Project Bond"
						name="projectBond"
						valuePropName="checked"
					>
						<Checkbox disabled>Yes/No</Checkbox>
					</Form.Item>
				</Col>
				<Form.Item
					noStyle
					shouldUpdate={(prevValues, currentValues) =>
						prevValues.projectBond !== currentValues.projectBond
					}
				>
					{({ getFieldValue }) =>
						getFieldValue("projectBond") && (
							<Col span={4}>
								<Form.Item
									label="Bond Evidence"
									name="projectBondEvidence"
									valuePropName="fileList"
									getValueFromEvent={normFile}
									rules={[
										{
											required: true,
											message: "Evidence is required",
										},
									]}
								>
									<Upload
										beforeUpload={() => false}
										maxCount={1}
										multiple={false}
										accept=".png, .jpg, .jpeg, .pdf"
										disabled={true}
									>
										<Button
											icon={<UploadOutlined />}
											size="large"
											style={{
												width: "100%",
											}}
											type="primary"
											disabled
										>
											Upload
										</Button>
									</Upload>
								</Form.Item>
							</Col>
						)
					}
				</Form.Item>
			</Row>
		</Form>
	);

	return (
		<div>
			<Flex vertical gap={"middle"}>
				{loading ? <CustomSpinner /> : <CustomForm />}
			</Flex>
		</div>
	);
};

export default JobProjectSheet;
